<script setup>
import { ref, computed } from 'vue'
import { marked } from 'marked'
import { withTrailingSlash } from '@/utils/siteUrl'

const props = defineProps({
  item:    { type: Object, required: true },   // { title, body, open }
  panelId: { type: String, required: true },
})

const open = ref(!!props.item.open)
// Internal links normalized to carry the trailing slash the host's redirect
// expects (GSC indexing fix, 2026-09-24) — mirrors RichTextBlock.vue's same fix.
const bodyHtml = computed(() =>
  marked.parse(props.item.body || '').replace(
    /<a href="(\/[^"]*)"/g,
    (match, href) => `<a href="${withTrailingSlash(href)}"`
  )
)
</script>

<template>
  <div class="accordion-item" :class="{ open }">
    <h3 class="header-row">
      <button
        type="button"
        class="trigger"
        :aria-expanded="open"
        :aria-controls="panelId"
        @click="open = !open"
      >
        <span class="title">{{ item.title }}</span>
        <span class="icon" aria-hidden="true">
          <svg viewBox="0 0 20 20" width="20" height="20" fill="none">
            <path d="M5 7.5 10 12.5 15 7.5" stroke="currentColor" stroke-width="2"
              stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </span>
      </button>
    </h3>
    <div :id="panelId" class="panel" v-show="open">
      <div class="prose" v-html="bodyHtml" />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.accordion-item {
  background: var(--color-surface-warm);
  border: 1px solid var(--color-border);
  border-radius: $radius;
  overflow: hidden;

  .header-row {
    margin: 0;
    font: inherit;
  }

  .trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $space-4;
    width: 100%;
    padding: $space-5;
    background: none;
    border: none;
    cursor: pointer;
    text-align: left;
    font-family: $font-sans;
    color: var(--color-ink);
    @include transition(background);

    // Reuses --color-fill-subtle (not a fresh rgba($color-ink, ...) wash) —
    // that token is already a subtle dark-ink tint in light mode and a
    // subtle light-paper tint in dark mode, so the hover wash stays visible
    // in both themes instead of freezing to an invisible indigo-on-dark
    // wash (jk-9-dark-mode.md fix wave).
    &:hover         { background: var(--color-fill-subtle); }
    &:focus-visible { @include focus-ring; }
    &:active        { transform: translateY(1px); }

    .title {
      font-size: $font-size-h4;
      font-weight: 500;
      line-height: $lh-heading-sm;
    }

    .icon {
      flex: none;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      border-radius: $radius-pill;
      background: var(--color-bg);
      color: var(--color-ink);
      @include transition(transform);
    }
  }

  &.open .trigger .icon { transform: rotate(180deg); }

  .panel {
    padding: 0 $space-5 $space-5;

    .prose {
      font-size: $font-size-base;
      line-height: $lh-base;
      color: var(--color-ink);

      :deep(p)  { margin: 0 0 $space-4; &:last-child { margin-bottom: 0; } }
      :deep(ul), :deep(ol) { margin: 0 0 $space-4 $space-5; }
      :deep(a)  { color: var(--color-primary); text-decoration: underline; }
    }
  }
}
</style>
